import React, { useEffect, useRef, useState, useCallback } from 'react';
import { callsAPI } from '../../utils/api';
import Avatar from '../UI/Avatar';

/**
 * 1-on-1 audio/video calls with real WebRTC.
 *
 * How this works (caller and receiver are both this component, just different props):
 *
 *   1. Caller hits POST /calls?action=initiate → gets a call_id.
 *   2. Caller calls getUserMedia → creates RTCPeerConnection → addTrack.
 *   3. Caller polls /calls?action=status — waits for receiver to "accept".
 *   4. Receiver clicks Accept → calls getUserMedia → creates RTCPeerConnection → addTrack.
 *   5. Receiver POSTs /calls?action=accept → status becomes "answered".
 *   6. Caller sees "answered" via status poll, runs createOffer + setLocalDescription,
 *      sends the offer as a signal.
 *   7. Receiver polls signals, gets the offer, runs setRemoteDescription + createAnswer +
 *      setLocalDescription, sends the answer.
 *   8. Caller polls signals, gets the answer, runs setRemoteDescription.
 *   9. ICE candidates fly back and forth via the same signal channel until the connection
 *      is established. ontrack fires; remote video is attached.
 *  10. Either side hangs up → sends "bye" signal + POST /calls?action=end → both clean up.
 *
 * Anything before step 6 happens on **both** sides locally; step 6 onwards is the
 * actual SDP/ICE handshake. Connection typically completes in 3-5 seconds with
 * 1.2s polling.
 */

// Free public STUN servers from Google. No TURN — calls fail for ~15-20% of users
// on strict NATs (carrier-grade NAT, locked-down corporate Wi-Fi). To add TURN later,
// extend this array with { urls: 'turn:turn.example.com:3478', username, credential }.
const ICE_SERVERS = [
  { urls: 'stun:stun.l.google.com:19302' },
  { urls: 'stun:stun1.l.google.com:19302' },
];

const SIGNAL_POLL_MS = 1200;   // how often we look for new signals from the other side
const STATUS_POLL_MS = 1500;   // how often we look for accepted/declined
const RING_TIMEOUT_MS = 45000; // give up after 45s of no answer

export default function CallScreen({ conv, callType, isIncoming = false, incomingCall = null, onEnd }) {
  // ─── Refs that must outlive renders without triggering them ───────────────
  const localVideoRef   = useRef(null);
  const remoteVideoRef  = useRef(null);
  const remoteAudioRef  = useRef(null);
  const localStreamRef  = useRef(null);
  const pcRef           = useRef(null);
  const callIdRef       = useRef(incomingCall?.id || null);
  const startTimeRef    = useRef(null);
  const lastSignalIdRef = useRef(0);
  const signalPollRef   = useRef(null);
  const statusPollRef   = useRef(null);
  const ringTimeoutRef  = useRef(null);
  const pendingIceRef   = useRef([]);          // ICE buffered until setRemoteDescription succeeds
  const remoteDescSetRef = useRef(false);
  const endedRef        = useRef(false);       // guard against double-cleanup
  const initRanRef      = useRef(false);       // guard against StrictMode dev double-invoke

  // ─── Reactive UI state ────────────────────────────────────────────────────
  const [status, setStatus]     = useState(isIncoming ? 'incoming' : 'calling');
  const [duration, setDuration] = useState(0);
  const [muted, setMuted]       = useState(false);
  const [camOff, setCamOff]     = useState(false);
  const [hasRemoteVideo, setHasRemoteVideo] = useState(false);

  const isVideo  = callType === 'video';
  const otherUser = isIncoming
    ? { name: incomingCall?.caller_name, avatar: incomingCall?.caller_avatar }
    : (conv?.other_user || { name: conv?.name });

  // ─── End-call cleanup ─────────────────────────────────────────────────────
  // Stable ref so we can call from many places without dependency churn.
  const cleanup = useCallback(() => {
    if (endedRef.current) return;
    endedRef.current = true;
    clearInterval(signalPollRef.current);
    clearInterval(statusPollRef.current);
    clearTimeout(ringTimeoutRef.current);
    if (pcRef.current) {
      try { pcRef.current.ontrack = null; pcRef.current.onicecandidate = null; pcRef.current.close(); }
      catch {}
      pcRef.current = null;
    }
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach(t => { try { t.stop(); } catch {} });
      localStreamRef.current = null;
    }
    if (localVideoRef.current)  localVideoRef.current.srcObject  = null;
    if (remoteVideoRef.current) remoteVideoRef.current.srcObject = null;
    if (remoteAudioRef.current) remoteAudioRef.current.srcObject = null;
  }, []);

  // ─── Hang up — signals the peer, ends the call server-side, cleans up ─────
  const handleEnd = useCallback(async (overrideStatus) => {
    const finalStatus = overrideStatus
      || (status === 'connected' ? 'answered'
      :  status === 'incoming'   ? 'missed'
      :  status === 'ringing'    ? 'missed'
      :  'missed');
    const dur = startTimeRef.current ? Math.floor((Date.now() - startTimeRef.current) / 1000) : 0;

    // Best-effort tell the other side we're gone (they'll also notice their PC closing,
    // but the explicit bye is faster than waiting for ICE to time out).
    if (callIdRef.current && !endedRef.current) {
      try { await callsAPI.signal(callIdRef.current, 'bye', { reason: finalStatus }); } catch {}
      try { await callsAPI.end(callIdRef.current, finalStatus, dur); } catch {}
    }
    cleanup();
    onEnd?.();
  }, [status, cleanup, onEnd]);

  // ─── getUserMedia — grabs camera + mic ────────────────────────────────────
  const startMedia = useCallback(async () => {
    if (localStreamRef.current) return localStreamRef.current;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: true,
        video: isVideo ? { width: { ideal: 1280 }, height: { ideal: 720 }, facingMode: 'user' } : false,
      });
      localStreamRef.current = stream;
      if (localVideoRef.current && isVideo) {
        localVideoRef.current.srcObject = stream;
      }
      return stream;
    } catch (err) {
      console.error('[call] getUserMedia failed:', err);
      setStatus('failed');
      return null;
    }
  }, [isVideo]);

  // ─── Build the RTCPeerConnection ──────────────────────────────────────────
  // Wires up:
  //   - ontrack: when the remote side's audio/video arrives, attach it to the <video>/<audio>
  //   - onicecandidate: each candidate we discover gets posted as a signal to the other side
  //   - oniceconnectionstatechange: detect failure / disconnection
  const buildPeerConnection = useCallback(() => {
    const pc = new RTCPeerConnection({ iceServers: ICE_SERVERS });

    pc.ontrack = (e) => {
      // The first stream contains both audio + video tracks (when video). Attach to
      // remote elements. When audio-only, the <video> would still work but we use
      // a separate <audio> element so the call screen stays a clean voice UI.
      const [stream] = e.streams;
      if (!stream) return;
      const hasVideoTrack = stream.getVideoTracks().length > 0;
      if (hasVideoTrack && remoteVideoRef.current) {
        remoteVideoRef.current.srcObject = stream;
        setHasRemoteVideo(true);
      }
      if (remoteAudioRef.current) {
        // Always attach to audio element too — this ensures audio plays even on browsers
        // that pause <video> when not in viewport.
        remoteAudioRef.current.srcObject = stream;
      }
    };

    pc.onicecandidate = (e) => {
      if (!e.candidate) return;        // null candidate signals "end of candidates"
      if (!callIdRef.current) return;
      callsAPI.signal(callIdRef.current, 'ice', e.candidate.toJSON()).catch(() => {});
    };

    pc.oniceconnectionstatechange = () => {
      const s = pc.iceConnectionState;
      if (s === 'connected' || s === 'completed') {
        if (status !== 'connected') {
          setStatus('connected');
          startTimeRef.current = startTimeRef.current || Date.now();
        }
      } else if (s === 'failed') {
        // Most common cause: STUN-only with strict NAT. We don't have a TURN
        // fallback in this build, so report and hang up.
        console.warn('[call] ICE failed — likely strict NAT. Need a TURN server.');
        setStatus('failed');
        setTimeout(() => handleEnd('failed'), 1500);
      } else if (s === 'disconnected') {
        // Brief network blip — give it a few seconds before giving up.
        setTimeout(() => {
          if (pcRef.current && pcRef.current.iceConnectionState === 'disconnected') {
            handleEnd('answered');
          }
        }, 5000);
      }
    };

    return pc;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ─── Apply a signal received from the other side ──────────────────────────
  const applySignal = useCallback(async (sig) => {
    const pc = pcRef.current;
    if (!pc) return;
    try {
      if (sig.signal_type === 'offer') {
        await pc.setRemoteDescription(new RTCSessionDescription(sig.payload));
        remoteDescSetRef.current = true;
        await flushPendingIce();
        const answer = await pc.createAnswer();
        await pc.setLocalDescription(answer);
        await callsAPI.signal(callIdRef.current, 'answer', answer);
      }
      else if (sig.signal_type === 'answer') {
        await pc.setRemoteDescription(new RTCSessionDescription(sig.payload));
        remoteDescSetRef.current = true;
        await flushPendingIce();
      }
      else if (sig.signal_type === 'ice') {
        if (!remoteDescSetRef.current) {
          // Receiver of glare can get ICE before the offer/answer round trip completes.
          // Buffer it.
          pendingIceRef.current.push(sig.payload);
          return;
        }
        try { await pc.addIceCandidate(new RTCIceCandidate(sig.payload)); }
        catch (e) { console.warn('[call] addIceCandidate failed:', e); }
      }
      else if (sig.signal_type === 'bye') {
        handleEnd(sig.payload?.reason || 'answered');
      }
    } catch (err) {
      console.error('[call] applySignal error:', err, sig);
    }
  }, [handleEnd]);

  const flushPendingIce = useCallback(async () => {
    const pc = pcRef.current;
    if (!pc) return;
    while (pendingIceRef.current.length) {
      const c = pendingIceRef.current.shift();
      try { await pc.addIceCandidate(new RTCIceCandidate(c)); }
      catch (e) { console.warn('[call] flushed addIceCandidate failed:', e); }
    }
  }, []);

  // ─── Start the signal polling loop ───────────────────────────────────────
  // Polls /signals?after_id=… every SIGNAL_POLL_MS, applies each new signal in order.
  const startSignalPolling = useCallback(() => {
    if (signalPollRef.current) return;
    signalPollRef.current = setInterval(async () => {
      if (!callIdRef.current || endedRef.current) return;
      try {
        const r = await callsAPI.pollSignals(callIdRef.current, lastSignalIdRef.current);
        const list = r.data?.data?.signals || [];
        for (const sig of list) {
          lastSignalIdRef.current = Math.max(lastSignalIdRef.current, sig.id);
          await applySignal(sig);
        }
      } catch {}
    }, SIGNAL_POLL_MS);
  }, [applySignal]);

  // ═══════════════════════════════════════════════════════════════════════════
  // OUTGOING CALL — caller side
  // 1) initiate → grab call_id
  // 2) getUserMedia + buildPeerConnection + addTrack
  // 3) start signal polling
  // 4) poll status — when receiver clicks accept, we createOffer
  // 5) receiver's answer arrives via signal → setRemoteDescription → media flows
  // ═══════════════════════════════════════════════════════════════════════════
  useEffect(() => {
    if (isIncoming) return;
    if (initRanRef.current) return;
    initRanRef.current = true;

    let createdOffer = false;
    const init = async () => {
      try {
        const r = await callsAPI.initiate(conv?.id, callType);
        if (r.data?.success) callIdRef.current = r.data.data.call_id;
        else { setStatus('failed'); return; }
      } catch {
        setStatus('failed'); return;
      }

      const stream = await startMedia();
      if (!stream) return;

      const pc = buildPeerConnection();
      pcRef.current = pc;
      stream.getTracks().forEach(t => pc.addTrack(t, stream));

      setStatus('ringing');
      startSignalPolling();

      // Ring-timeout — if nobody answers in 45s, give up.
      ringTimeoutRef.current = setTimeout(() => {
        if (status === 'ringing' || status === 'calling') handleEnd('missed');
      }, RING_TIMEOUT_MS);

      // Poll for receiver's accept / decline. We don't create the offer until
      // they accept, otherwise their POST /accept races with our offer arrival.
      statusPollRef.current = setInterval(async () => {
        if (!callIdRef.current || endedRef.current) return;
        try {
          const r = await callsAPI.status(callIdRef.current);
          const s = r.data?.data?.status;
          if (s === 'answered' && !createdOffer) {
            createdOffer = true;
            const offer = await pc.createOffer();
            await pc.setLocalDescription(offer);
            await callsAPI.signal(callIdRef.current, 'offer', offer);
            // Status will flip to 'connected' once ICE actually connects (see
            // oniceconnectionstatechange). Until then the UI keeps showing
            // "ringing" briefly while SDP exchanges.
            clearInterval(statusPollRef.current);
          } else if (s === 'declined') {
            setStatus('declined');
            setTimeout(() => handleEnd('declined'), 1500);
          } else if (s === 'missed') {
            setStatus('missed');
            setTimeout(() => handleEnd('missed'), 1500);
          }
        } catch {}
      }, STATUS_POLL_MS);
    };
    init();

    return () => { cleanup(); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ═══════════════════════════════════════════════════════════════════════════
  // INCOMING CALL — receiver side
  // We don't do anything until the user clicks Accept. handleAccept() does:
  //   - getUserMedia + buildPeerConnection + addTrack
  //   - POST /accept (flips DB row to 'answered'; that wakes up the caller)
  //   - start signal polling — caller's offer will arrive here
  // ═══════════════════════════════════════════════════════════════════════════
  const handleAccept = useCallback(async () => {
    if (!callIdRef.current) return;

    const stream = await startMedia();
    if (!stream) return;

    const pc = buildPeerConnection();
    pcRef.current = pc;
    stream.getTracks().forEach(t => pc.addTrack(t, stream));

    try { await callsAPI.accept(callIdRef.current); } catch {}
    setStatus('connecting');
    startSignalPolling();
  }, [startMedia, buildPeerConnection, startSignalPolling]);

  const handleDecline = useCallback(async () => {
    if (callIdRef.current) {
      try { await callsAPI.decline(callIdRef.current); } catch {}
    }
    cleanup();
    onEnd?.();
  }, [cleanup, onEnd]);

  // ─── Duration counter (only while connected) ─────────────────────────────
  useEffect(() => {
    if (status !== 'connected') return;
    const iv = setInterval(() => {
      setDuration(Math.floor((Date.now() - (startTimeRef.current || Date.now())) / 1000));
    }, 1000);
    return () => clearInterval(iv);
  }, [status]);

  // ─── Mute / camera toggles operate on the actual track flags ─────────────
  const toggleMute = () => {
    const next = !muted;
    setMuted(next);
    if (localStreamRef.current) {
      localStreamRef.current.getAudioTracks().forEach(t => { t.enabled = !next; });
    }
  };
  const toggleCamera = () => {
    const next = !camOff;
    setCamOff(next);
    if (localStreamRef.current) {
      localStreamRef.current.getVideoTracks().forEach(t => { t.enabled = !next; });
    }
  };

  const formatDur = (s) => `${String(Math.floor(s / 60)).padStart(2,'0')}:${String(s % 60).padStart(2,'0')}`;

  // ─── Render ──────────────────────────────────────────────────────────────
  return (
    <div className="call-screen">
      {/* Remote audio is always rendered, off-screen — so audio plays even when
          the user has scrolled past or the video element is hidden. */}
      <audio ref={remoteAudioRef} autoPlay playsInline />

      {/* Remote video, full-bleed when present */}
      {isVideo && (
        <video
          ref={remoteVideoRef}
          className="call-remote-video"
          autoPlay
          playsInline
          style={{ opacity: hasRemoteVideo && status === 'connected' ? 1 : 0 }}
        />
      )}

      {/* Local self-view in the corner */}
      {isVideo && (
        <div className="call-local-video">
          <video ref={localVideoRef} autoPlay playsInline muted />
        </div>
      )}

      {/* Identity / status overlay */}
      <div className="call-info" style={{
        opacity: hasRemoteVideo && status === 'connected' && isVideo ? 0 : 1,
        transition: 'opacity 0.3s',
      }}>
        <div className="avatar-call"><Avatar user={otherUser} size={110} /></div>
        <h2 style={{ fontSize: 24, fontWeight: 800 }}>{otherUser?.name || 'Unknown'}</h2>
        <p style={{ color: 'rgba(255,255,255,.75)', fontSize: 15, marginTop: 6 }}>
          {status === 'calling'    && (isVideo ? '📹 Video calling…' : '📞 Calling…')}
          {status === 'ringing'    && '🔔 Ringing…'}
          {status === 'connecting' && '⚙️ Connecting…'}
          {status === 'incoming'   && `Incoming ${isVideo ? 'video' : 'voice'} call`}
          {status === 'connected'  && `🟢 ${formatDur(duration)}`}
          {status === 'failed'     && '❌ Call failed — check microphone / camera permissions'}
          {status === 'declined'   && '📵 Call declined'}
          {status === 'missed'     && '📵 No answer'}
        </p>
      </div>

      {/* Controls */}
      <div className="call-controls">
        {status === 'incoming' ? (
          <>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
              <button className="call-btn" style={{ background: '#10B981', border: 'none', width: 70, height: 70, fontSize: 28 }} onClick={handleAccept}>📞</button>
              <span style={{ fontSize: 12, color: 'rgba(255,255,255,.7)' }}>Accept</span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
              <button className="call-btn end" style={{ width: 70, height: 70, fontSize: 28 }} onClick={handleDecline}>📵</button>
              <span style={{ fontSize: 12, color: 'rgba(255,255,255,.7)' }}>Decline</span>
            </div>
          </>
        ) : (
          <>
            <button className={`call-btn ${muted ? 'muted' : ''}`} onClick={toggleMute} title={muted ? 'Unmute' : 'Mute'}>
              {muted ? '🔇' : '🎤'}
            </button>
            {isVideo && (
              <button className={`call-btn ${camOff ? 'muted' : ''}`} onClick={toggleCamera} title={camOff ? 'Camera on' : 'Camera off'}>
                {camOff ? '📷' : '📸'}
              </button>
            )}
            <button className="call-btn end" onClick={() => handleEnd()} title="End call">📵</button>
          </>
        )}
      </div>
    </div>
  );
}
